import {
  Box,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material'
import Card from 'components/@extended/Card'
import DebitCreditReviewTable from 'components/PeriodClosureDialog/DebitCreditReviewTable'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import { useAccrualLedger } from 'hooks/useAccruals'
import useAuth from 'hooks/useAuth'
import { useFetchAllLedgerAccounts } from 'hooks/useLedgerAccounts'
import useLocalStorage from 'hooks/useLocalStorage'
import { usePeriods } from 'hooks/usePeriods'
import React, { Suspense, useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import type { AccrualLedgerQueryParams, PrepareLedgerItem } from 'types/accruals'
import type { Period } from 'types/periods'
import { createMergedIdLabelMap } from 'types/periods'

const Ledger: React.FC = () => {
  const theme = useTheme()
  const { token, profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'ledger-selected-organizations',
    [],
    profile?.user?.id
  )
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'ledger-selected-periods',
    [],
    profile?.user?.id
  )
  const [periods, setPeriods] = useState<Period[]>([])
  const [isPending, startTransition] = useTransition()
  const [periodLabels, setPeriodLabels] = useState<Record<string, string>>({})
  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedLedgerAccount, setSelectedLedgerAccount] = useState<string>('All')

  const { ledgerAccounts, isLoading: isLoadingLedgerAccounts } = useFetchAllLedgerAccounts(
    selectedOrganizations,
    token!,
    selectedOrganizations.length > 0 && !!token
  )

  const { periods: fetchedPeriods, isLoadingPeriods } = usePeriods(
    token!,
    selectedOrganizations[0] || '',
    !!token && selectedOrganizations.length > 0
  )

  const queryParams: AccrualLedgerQueryParams = {
    organizationIds: selectedOrganizations,
    periodIds: selectedPeriods,
    searchTerm,
    sort: [{ field: 'userLastName', order: 'asc' }],
    pagination: {
      page: 1,
      perPage: 1000, // Get more data since we're not paginating in the new table
    },
    ledgerAccountId: selectedLedgerAccount !== 'All' ? selectedLedgerAccount : undefined,
  }

  const {
    accrualLedgers = [],
    isLoading,
    isError,
    refetch: refetchLedger,
  } = useAccrualLedger({
    token: token!,
    queryParams,
    shouldFetch: !!token && selectedOrganizations.length > 0 && selectedPeriods.length > 0 && isInitialized,
  })

  useEffect(() => {
    if (fetchedPeriods) {
      setPeriods(fetchedPeriods.periods || [])
    }
  }, [fetchedPeriods])

  useEffect(() => {
    if (fetchedPeriods && fetchedPeriods.periods.length > 0) {
      const mergedLabelMap = createMergedIdLabelMap(fetchedPeriods.periods)
      setPeriodLabels(mergedLabelMap)
    }
  }, [fetchedPeriods])

  useEffect(() => {
    if (profile && profile.roles.length > 0 && !isInitialized) {
      const allOrganizationIds = profile.roles.map((role) => role.organization.id)
      if (selectedOrganizations.length === 0) {
        setSelectedOrganizations(allOrganizationIds)
      }
      setIsInitialized(true)
    }
  }, [profile, isInitialized])

  // Add visibility change handler to refresh data when returning to page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchLedger()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Initial data fetch when component mounts
    if (isInitialized && token && selectedOrganizations.length > 0 && selectedPeriods.length > 0) {
      refetchLedger()
    }

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refetchLedger, isInitialized, token, selectedOrganizations.length, selectedPeriods.length])

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  // Transform accrual ledger data to PrepareLedgerItem format
  const lineItems: PrepareLedgerItem[] = useMemo(() => {
    return accrualLedgers.map((ledgerEntry) => ({
      id: ledgerEntry.id,
      userId: ledgerEntry.userId,
      firstName: ledgerEntry.userFirstName,
      lastName: ledgerEntry.userLastName,
      rootOrganizationId: ledgerEntry.rootOrganizationId,
      organizationId: ledgerEntry.organizationId,
      ledgerAccountId: ledgerEntry.ledgerAccountId,
      planId: ledgerEntry.planId,
      planRevision: ledgerEntry.planRevision || 1,
      allocationTableId: ledgerEntry.allocationTableId,
      allocationTableName: ledgerEntry.allocationTableName,
      allocationRowId: ledgerEntry.allocationRowId,
      allocationRowName: ledgerEntry.allocationRowName,
      rootPeriodId: ledgerEntry.rootPeriodId,
      periodId: ledgerEntry.periodId,
      balance: Number(ledgerEntry.balance),
      previousBalance: Number(ledgerEntry.previousBalance),
      createdAt: ledgerEntry.createdAt || new Date().toISOString(),
      updatedAt: ledgerEntry.updatedAt || new Date().toISOString(),
    }))
  }, [accrualLedgers])

  // Create empty states for read-only view
  const adjustments = useMemo(() => ({}), [])
  const checkedItems = useMemo(() => {
    const checked: Record<string, boolean> = {}
    lineItems.forEach((item) => {
      const id = item.id || `${item.allocationTableId}-${item.allocationRowId}`
      checked[id] = true
    })
    return checked
  }, [lineItems])
  const notes = useMemo(() => {
    const noteMap: Record<string, string> = {}
    accrualLedgers.forEach((ledgerEntry) => {
      const id = ledgerEntry.id
      noteMap[id] = ledgerEntry.notes || ''
    })
    return noteMap
  }, [accrualLedgers])

  // Calculate summary totals
  const getTotalCredits = useCallback(() => {
    const total = lineItems.reduce((sum, item) => {
      const change = item.balance - item.previousBalance
      return sum + (change > 0 ? change : 0)
    }, 0)
    return total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }, [lineItems])

  const getTotalDebits = useCallback(() => {
    const total = lineItems.reduce((sum, item) => {
      const change = item.balance - item.previousBalance
      return sum + (change < 0 ? Math.abs(change) : 0)
    }, 0)
    return total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }, [lineItems])

  const getNetChange = useCallback(() => {
    const credits = parseFloat(getTotalCredits().replace(/,/g, ''))
    const debits = parseFloat(getTotalDebits().replace(/,/g, ''))
    return (credits - debits).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }, [getTotalCredits, getTotalDebits])

  const handleOrganizationChange = (newSelectedOrganizations: string[]) => {
    startTransition(() => {
      setSelectedOrganizations(newSelectedOrganizations)
    })
  }

  const handlePeriodChange = (newSelectedPeriods: string[]) => {
    startTransition(() => {
      setSelectedPeriods(newSelectedPeriods)
    })
  }

  return (
    <Box sx={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', p: 3, overflow: 'hidden' }}>
      <Grid container pb={2} spacing={2} sx={{ flexShrink: 0 }}>
        <Grid size={{ xs: 6 }}>
          <SimpleTitle title="Accrual Ledger" />
        </Grid>
      </Grid>
      <Grid container spacing={4} mb={3} sx={{ flexShrink: 0 }}>
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="ledger-search-input"
            key="ledger-search-input"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel shrink sx={{ '&.MuiInputLabel-shrink': { background: theme.palette.background.default } }}>
              Ledger Accounts
            </InputLabel>
            <Select
              fullWidth
              value={selectedLedgerAccount}
              onChange={(e) => setSelectedLedgerAccount(e.target.value as string)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  },
                },
              }}
              style={{ borderRadius: '21px' }}
            >
              <MenuItem value="All">All</MenuItem>
              {isLoadingLedgerAccounts ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : (
                ledgerAccounts?.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 3 }}>
          <MultiPeriodSelector
            key="period-selector"
            periods={periods}
            selectedPeriods={selectedPeriods}
            handleChange={handlePeriodChange}
            rootPeriodOnly={false}
            sortOrder="asc"
            timeFilter="pastOnly"
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>

        <Grid size={{ xs: 4 }}>
          <MultiOrganizationSelector
            key="organization-selector"
            userProfile={profile!}
            selectedOrganizationIds={selectedOrganizations}
            handleChange={handleOrganizationChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
      </Grid>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <Suspense fallback={<CircularProgress />}>
          {isLoadingPeriods || isLoading || isPending || isLoadingLedgerAccounts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography color="error">Error loading ledger entries</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  flex: '1 1 0',
                  maxWidth: 'calc(100% - 300px)',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <DebitCreditReviewTable
                  lineItems={lineItems}
                  adjustments={adjustments}
                  checkedItems={checkedItems}
                  notes={notes}
                  periodIdToLabel={periodLabels}
                  isLoading={isLoading}
                />
              </Box>
              <Box sx={{ width: 280, flexShrink: 0 }}>
                <Card variant="outlined" sx={{ height: 'fit-content' }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                      Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Credits:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          ${getTotalCredits()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Debits:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          ${getTotalDebits()}
                        </Typography>
                      </Box>
                      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            Balance:
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            ${getNetChange()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Box>
          )}
        </Suspense>
      </Box>
    </Box>
  )
}

export default Ledger
